import { useMemo } from 'react';
import { DEFAULT_BRANDING_THEME, type BrandingTheme } from '@fms/contracts';

/**
 * Publishes the authored brand colours as CSS custom properties.
 *
 * A `<style>` element rather than inline styles on `<html>`, and that is the
 * whole design: inline custom properties beat every stylesheet rule, which would
 * silently defeat the `[data-contrast='high']` modifier in tokens.css and drop
 * the portal below AA for anyone using forced high contrast. A stylesheet plays
 * by specificity instead — the high-contrast selectors are more specific and
 * keep winning, while these rules override the plain `:root` defaults.
 *
 * Renders nothing at all while the colours are the shipped defaults, which is
 * the common case: no extra element, no extra parse.
 */
export interface BrandingThemeVarsProps {
  theme: BrandingTheme;
}

/** Multiply a #rrggbb by a factor, for a hover shade one step down. */
function shade(hex: string, factor: number): string {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!match) return hex;
  const channel = (part: string) =>
    Math.max(0, Math.min(255, Math.round(parseInt(part, 16) * factor)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(match[1]!)}${channel(match[2]!)}${channel(match[3]!)}`;
}

function isDefault(theme: BrandingTheme): boolean {
  return (
    theme.gold === DEFAULT_BRANDING_THEME.gold &&
    theme.goldDeep === DEFAULT_BRANDING_THEME.goldDeep &&
    theme.goldInk === DEFAULT_BRANDING_THEME.goldInk &&
    theme.goldInkDark === DEFAULT_BRANDING_THEME.goldInkDark
  );
}

export function BrandingThemeVars({ theme }: BrandingThemeVarsProps) {
  const css = useMemo(() => {
    if (isDefault(theme)) return '';
    const gradient = `linear-gradient(180deg, ${theme.gold} 0%, ${theme.goldDeep} 100%)`;
    const gradientHover = `linear-gradient(180deg, ${theme.goldDeep} 0%, ${shade(theme.goldDeep, 0.86)} 100%)`;
    // MUI's cssVariables theme emits its palette under both `:root` and the
    // per-scheme attribute selectors, so the override has to name all three to
    // win the specificity comparison in either scheme.
    const muiScopes =
      ':root,:root[data-mui-color-scheme="light"],:root[data-mui-color-scheme="dark"]';
    return [
      `:root{`,
      `--fms-color-gold:${theme.gold};`,
      `--fms-color-gold-deep:${theme.goldDeep};`,
      `--fms-color-gold-ink:${theme.goldInk};`,
      `--fms-color-gold-on-scrim:${theme.goldInkDark};`,
      `--fms-gold-gradient:${gradient};`,
      `--fms-gold-gradient-hover:${gradientHover};`,
      `}`,
      `:root[data-mui-color-scheme="dark"]{--fms-color-gold-ink:${theme.goldInkDark};}`,
      `${muiScopes}{`,
      `--mui-palette-primary-main:${theme.gold};`,
      `--mui-palette-primary-dark:${theme.goldDeep};`,
      `}`,
    ].join('');
  }, [theme]);

  if (!css) return null;
  return <style data-fms-branding="theme">{css}</style>;
}
